import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Leaf, Recycle, Sparkles, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=800&q=80";

export default function OurStory() {
  const [storyImage, setStoryImage] = useState(FALLBACK_IMAGE);

  useEffect(() => {
    axios.get(`${API}/settings/story-image`).then(res => {
      if (res.data.image_url) setStoryImage(res.data.image_url);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <SEO
        title="Our Story"
        description="Discover Mahra's mission to make luxury Indian fashion accessible in Melbourne through sustainable outfit rentals. Our story, our values, our community."
        path="/our-story"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Our Story" }]}
      />
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      
      <Navbar />

      {/* Hero Section */}
      <section className="px-6 pt-12 pb-20 lg:px-16 lg:pt-20 lg:pb-28">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#B8C4B8] text-[11px] tracking-[0.3em] uppercase mb-4">Our Story</p>
          <h1 className="text-4xl lg:text-6xl text-[#676664] mb-8 leading-tight">
            Beautiful Outfits Deserve<br />
            <span className="italic font-light">More Than One Memory</span>
          </h1>
          <p className="text-[#676664]/70 text-lg leading-relaxed font-light max-w-2xl mx-auto">
            Mahra started with a simple realization: our closets were full of stunning outfits 
            worn only once, hanging untouched while we bought more for the next event.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="px-6 py-16 lg:px-16 lg:py-24 bg-[#6B5B4F]">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-4">The Problem</p>
              <h2 className="text-3xl lg:text-4xl text-white mb-6">
                We've all been there
              </h2>
              <div className="space-y-4 text-white/70 font-light">
                <p>
                  You find the perfect lehenga for a wedding. You spend hours shopping, 
                  thousands of dollars, and it's absolutely stunning on the day.
                </p>
                <p>
                  Then it goes in the closet. And stays there.
                </p>
                <p>
                  Maybe you wear it once more, if you're lucky. Most likely, it becomes 
                  a beautiful reminder of a single day, taking up space while you stress 
                  about what to wear to the next event.
                </p>
              </div>
            </div>
            <div className="bg-[#F0D9D5]/10 p-8 rounded-lg">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#9A7B4F]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#9A7B4F] text-lg">$</span>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Average Cost</p>
                    <p className="text-white/60 text-sm">$500-$3000 for a quality Indian outfit worn just once</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#B8C4B8]/20 flex items-center justify-center flex-shrink-0">
                    <Recycle className="w-5 h-5 text-[#B8C4B8]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Closet Impact</p>
                    <p className="text-white/60 text-sm">92% of ethnic wear is worn less than 3 times</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#F0D9D5]/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-[#F0D9D5]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">The Guilt</p>
                    <p className="text-white/60 text-sm">That nagging feeling every time you pass by those unworn pieces</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="px-6 py-16 lg:px-16 lg:py-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={storyImage}
                alt="Beautiful outfit"
                className="w-full rounded-lg shadow-xl object-cover"
                style={{ maxHeight: '600px' }}
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#B8C4B8] text-[11px] tracking-[0.3em] uppercase mb-4">Our Solution</p>
              <h2 className="text-3xl lg:text-4xl text-[#676664] mb-6">
                What if you could borrow instead?
              </h2>
              <div className="space-y-4 text-[#676664]/70 font-light">
                <p>
                  Mahra is our wardrobe, shared with you. Every piece you see has been 
                  worn and loved by us — at our own weddings, festivals, and celebrations.
                </p>
                <p>
                  Instead of letting these beautiful outfits collect dust, we're giving 
                  them new life. And giving you access to high-quality pieces without 
                  the high cost or the closet guilt.
                </p>
                <p className="text-[#676664] font-medium">
                  It's like borrowing from a friend — if your friend had really good taste 
                  and didn't mind at all.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="px-6 py-16 lg:px-16 lg:py-24 bg-[#B8C4B8]/15">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#B8C4B8] text-[11px] tracking-[0.3em] uppercase mb-3">What We Believe</p>
            <h2 className="text-3xl lg:text-4xl text-[#676664]">
              Fashion Should Be Circular
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-white rounded-full shadow-sm">
                <Leaf className="w-6 h-6 text-[#B8C4B8]" />
              </div>
              <h3 className="text-lg text-[#676664] mb-2">Sustainability</h3>
              <p className="text-sm text-[#676664]/60 font-light">
                Every borrow reduces fashion waste and extends the life of beautiful pieces
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-white rounded-full shadow-sm">
                <Users className="w-6 h-6 text-[#9A7B4F]" />
              </div>
              <h3 className="text-lg text-[#676664] mb-2">Community</h3>
              <p className="text-sm text-[#676664]/60 font-light">
                Sharing beautiful things with our community creates connections beyond transactions
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-white rounded-full shadow-sm">
                <Sparkles className="w-6 h-6 text-[#D4B8B4]" />
              </div>
              <h3 className="text-lg text-[#676664] mb-2">Accessibility</h3>
              <p className="text-sm text-[#676664]/60 font-light">
                Beautiful fashion shouldn't require big budgets
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-white rounded-full shadow-sm">
                <Heart className="w-6 h-6 text-[#9A7B4F]" />
              </div>
              <h3 className="text-lg text-[#676664] mb-2">Intentionality</h3>
              <p className="text-sm text-[#676664]/60 font-light">
                Every piece is hand-picked, cared for, and ready to create new memories
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-16 lg:px-16 lg:py-24 bg-[#F0D9D5]/30 relative">
        <div className="petal-corner-tl" />
        <div className="petal-corner-br" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl text-[#676664] mb-6">
            Ready to Borrow Something Beautiful?
          </h2>
          <p className="text-[#676664]/60 text-lg mb-10 font-light">
            Join us in making fashion more sustainable, one borrowed outfit at a time.
          </p>
          <Link to="/catalog">
            <Button 
              data-testid="browse-wardrobe-btn"
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-12 py-6 text-[11px] tracking-[0.2em] uppercase transition-all duration-300 rounded-full"
            >
              Explore Our Wardrobe
              <ArrowRight className="ml-3 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
